import { Box, Text } from '@chakra-ui/react';
import { ReactElement, ReactNode, RefObject, useId } from 'react';
import { CloseIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { Input } from 'components/ui/form/Input';

type MultiSelectSearchInputProps = {
  searchQuery: string;
  searchPlaceholder: string;
  searchInputRef: RefObject<HTMLInputElement>;
  onSearchChange: (value: string) => void;
  onClose: () => void;
  /** Shown below the field (e.g. server-side lists that are not fully loaded) */
  hint?: ReactNode;
};

/**
 * MultiSelectSearchInput component - Search input with clear button
 * Sticky at top of dropdown when searchable
 */
export const MultiSelectSearchInput = ({
  searchQuery,
  searchPlaceholder,
  searchInputRef,
  onSearchChange,
  onClose,
  hint,
}: MultiSelectSearchInputProps): ReactElement => {
  /** Override Field.Input’s default id so this portaled control stays distinct from `Field.Root` wiring. */
  const searchInputId = useId();
  return (
    <Box
      position="sticky"
      top="0"
      zIndex="10"
      bg="background.primary"
      px="15px"
      py="s"
      borderBottom="default"
      borderColor="border.accent"
    >
      <Box position="relative">
        <Input
          id={searchInputId}
          ref={searchInputRef}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onClose();
            }
            e.stopPropagation();
          }}
          _placeholder={{
            color: 'text.quaternary',
          }}
          _focus={{
            borderColor: 'border.accent.active',
            boxShadow: 'none',
          }}
        />
        {searchQuery && (
          <Box
            position="absolute"
            right="15px"
            top="50%"
            transform="translateY(-50%)"
            cursor="pointer"
            onClick={(e) => {
              e.stopPropagation();
              onSearchChange('');
              searchInputRef.current?.focus();
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            _hover={{ opacity: '70' }}
            w="16px"
            h="16px"
          >
            <NovaIcon icon={CloseIcon} size="s" color="icon.tertiary" aria-hidden />
          </Box>
        )}
      </Box>
      {hint && (
        <Text
          textStyle="body.xs.regular"
          color="text.secondary"
          lineHeight="1.35"
          pt="8px"
          pr="2px"
        >
          {hint}
        </Text>
      )}
    </Box>
  );
};
